import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IdentifierInput from './components/IdentifierInput'
import EvidencePanel from './components/EvidencePanel'
import CandidatesTable from './components/CandidatesTable'
import { previewConfirmationResend, resendConfirmation } from '../../../services/naturaConfirmationResendService'
import {
  ERRORS_WITH_RAW_DETAIL,
  ERROR_AMBIGUOUS_EXTERNAL_REFERENCE,
  ERROR_RESEND_IN_PROGRESS,
  KNOWN_REJECTIONS,
  MODE_EXTERNAL_REFERENCE,
  MODE_REPLENISHMENT_ID,
  NATURA_OUTCOME_ERRORS,
} from './constants'

const STATE_IDLE = 'idle'
const STATE_PREVIEWING = 'previewing'
const STATE_PREVIEW_READY = 'preview_ready'
const STATE_RESENDING = 'resending'
const STATE_DONE = 'done'

const FAILURE_NONE = null
const FAILURE_REJECTION = 'rejection'
const FAILURE_GENERIC = 'generic'
// A resend that fails without a body may still have reached SAP, so it cannot
// share the "nothing happened, try again" panel a failed preview uses.
const FAILURE_UNKNOWN_DELIVERY = 'unknown_delivery'

// A 502 body is only renderable as a result if it actually carries the round
// trip; without it there is nothing to show and it falls back to the generic panel.
const classify = error => {
  const data = error?.response?.data
  const code = data?.error

  if (NATURA_OUTCOME_ERRORS.includes(code) && data.request) return { kind: 'outcome', data }
  if (KNOWN_REJECTIONS.includes(code)) return { kind: FAILURE_REJECTION, data }
  return { kind: FAILURE_GENERIC }
}

export default function ConfirmationResendPage() {
  const { t } = useTranslation()
  const key = suffix => `natura_operations.confirmation_resend.${suffix}`

  const [mode, setMode] = useState(MODE_EXTERNAL_REFERENCE)
  const [identifier, setIdentifier] = useState('')

  const [state, setState] = useState(STATE_IDLE)
  const [preview, setPreview] = useState(null)
  const [outcome, setOutcome] = useState(null)

  const [failureKind, setFailureKind] = useState(FAILURE_NONE)
  const [rejection, setRejection] = useState(null)

  const isBusy = state === STATE_PREVIEWING || state === STATE_RESENDING

  // Any edit to the identifier invalidates the payload on screen, so a preview
  // built for one replenishment can never be resent against another.
  const invalidate = () => {
    setPreview(null)
    setOutcome(null)
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    setState(STATE_IDLE)
  }

  // Keeps the outcome: it records a POST that already happened, so unlike a
  // preview it cannot go stale, and one keystroke must not destroy the evidence.
  const discardPreview = () => {
    setPreview(null)
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    setState(STATE_IDLE)
  }

  const handleModeChange = next => {
    setMode(next)
    setIdentifier('')
    invalidate()
  }

  const handleIdentifierChange = value => {
    setIdentifier(value)
    discardPreview()
  }

  const selectCandidate = candidateId => {
    setMode(MODE_REPLENISHMENT_ID)
    setIdentifier(candidateId)
    invalidate()
  }

  const payload = () => ({ [mode]: identifier.trim() })

  // `sent` distinguishes the two callers: a preview that fails sent nothing, while
  // a resend that fails without a body may have reached SAP and must say so.
  const handleFailure = (error, { sent } = {}) => {
    const classified = classify(error)
    if (classified.kind === 'outcome') {
      setOutcome(classified.data)
      setFailureKind(FAILURE_NONE)
      return true
    }
    if (classified.kind === FAILURE_REJECTION) {
      setRejection(classified.data)
      setFailureKind(FAILURE_REJECTION)
      return false
    }
    setFailureKind(sent ? FAILURE_UNKNOWN_DELIVERY : FAILURE_GENERIC)
    return false
  }

  const generatePreview = async () => {
    if (isBusy || identifier.trim() === '') return
    setState(STATE_PREVIEWING)
    setPreview(null)
    setOutcome(null)
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    try {
      const data = await previewConfirmationResend(payload())
      setPreview(data)
      setState(STATE_PREVIEW_READY)
    } catch (e) {
      handleFailure(e)
      setState(STATE_IDLE)
    }
  }

  const resend = async () => {
    if (!preview || isBusy) return
    setState(STATE_RESENDING)
    setOutcome(null)
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    try {
      // Sent by resolved id, not by the typed identifier: the server re-resolves, and a
      // Natura number can start matching a different replenishment between the two calls.
      const data = await resendConfirmation({ replenishment_id: String(preview.replenishment_id) })
      setOutcome(data)
      setState(STATE_DONE)
    } catch (e) {
      const rendered = handleFailure(e, { sent: true })
      setState(rendered ? STATE_DONE : STATE_PREVIEW_READY)
    }
  }

  const rejectionHeading = () => t(key(`rejection_${rejection.error}`))
  const showRawDetail = ERRORS_WITH_RAW_DETAIL.includes(rejection?.error) && rejection?.message

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t(key('title'))}</h1>

      <section className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
        <h2 className="font-semibold mb-2">{t(key('instructions_heading'))}</h2>
        <ol className="list-decimal pl-6 text-sm space-y-1">
          <li>{t(key('instructions_step1'))}</li>
          <li>{t(key('instructions_step2'))}</li>
          <li>{t(key('instructions_step3'))}</li>
        </ol>
        <p className="mt-3 text-sm text-amber-700">{t(key('warning'))}</p>
      </section>

      {failureKind === FAILURE_REJECTION && rejection && (
        <div role="alert" className="mb-4 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p>{rejectionHeading()}</p>
          {rejection.error === ERROR_RESEND_IN_PROGRESS && rejection.retry_after_seconds && (
            <p className="mt-1">{t(key('retry_after'), { seconds: rejection.retry_after_seconds })}</p>
          )}
          {showRawDetail && <pre className="mt-2 text-xs whitespace-pre-wrap break-words max-h-60 overflow-auto">{rejection.message}</pre>}
          {rejection.error === ERROR_AMBIGUOUS_EXTERNAL_REFERENCE && (rejection.candidates || []).length > 0 && (
            <CandidatesTable candidates={rejection.candidates} onSelect={selectCandidate} />
          )}
        </div>
      )}

      <section className="mb-6">
        <IdentifierInput mode={mode} value={identifier} onModeChange={handleModeChange} onValueChange={handleIdentifierChange} disabled={isBusy} />
      </section>

      <div>
        <button
          type="button"
          onClick={generatePreview}
          disabled={isBusy || identifier.trim() === ''}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {state === STATE_PREVIEWING ? t(key('generating_preview')) : t(key('generate_preview'))}
        </button>
      </div>

      {failureKind === FAILURE_GENERIC && (
        <div role="alert" className="mt-6 p-4 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p className="mb-2">{t(key('error_generic'))}</p>
          <button type="button" onClick={generatePreview} className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white">
            {t(key('retry'))}
          </button>
        </div>
      )}

      {failureKind === FAILURE_UNKNOWN_DELIVERY && (
        <div role="alert" className="mt-6 p-4 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
          <p className="font-medium">{t(key('delivery_unknown'))}</p>
          <p className="mt-1">{t(key('delivery_unknown_explanation'))}</p>
          <p className="mt-2">{t(key('unknown_delivery_detail'))}</p>
          <button type="button" onClick={resend} className="mt-3 px-3 py-1.5 text-sm rounded border border-amber-600 text-amber-900 hover:bg-amber-100">
            {t(key('resend_again'))}
          </button>
        </div>
      )}

      {preview && !outcome && (
        <>
          <EvidencePanel data={preview} />
          {preview.qa_store && (
            <div role="status" className="mt-3 p-3 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
              {t(key('qa_store_notice'))}
            </div>
          )}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={resend}
              disabled={isBusy || preview.qa_store}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              {state === STATE_RESENDING ? t(key('resending')) : t(key('resend'))}
            </button>
            <button type="button" onClick={invalidate} disabled={isBusy} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
              {t(key('cancel'))}
            </button>
          </div>
        </>
      )}

      {outcome && (
        <>
          <EvidencePanel data={outcome} showOutcome />
          <div className="mt-4">
            <button
              type="button"
              onClick={resend}
              disabled={isBusy || !preview}
              className="px-4 py-2 rounded border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state === STATE_RESENDING ? t(key('resending')) : t(key('resend_again'))}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
